import React, { useState } from 'react';
import {
  Typography, Tag, Button, Space, Spin,
} from 'antd';
import {
  ShareAltOutlined, ShoppingCartOutlined,
  HeartOutlined, HeartFilled, ArrowLeftOutlined,
  ShopOutlined, CalendarOutlined, EditOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useGetDealQuery, useVoteMutation, useSaveDealMutation, useUnsaveDealMutation } from './dealsApi';
import PriceTag from '../../../../../packages/ui/src/PriceTag/index';
import VoteButtons from '../../../../../packages/ui/src/VoteButtons/index';
import PriceHistoryChart from '../../../../../packages/ui/src/PriceHistoryChart/index';
import CommentSection from '../community/CommentSection';

const { Title, Text, Paragraph } = Typography;

const CONDITION_LABELS = {
  new: 'New',
  refurbished: 'Refurbished',
  used: 'Used',
  used_open_box: 'Open Box',
  open_box: 'Open Box',
};

function entityLabel(value) {
  if (value == null) return null;
  if (typeof value === 'string') return value || null;
  if (typeof value === 'object') return value.name || null;
  return null;
}

function ImageGallery({ images }) {
  const [activeIndex, setActiveIndex] = useState(0);

  if (!images || images.length === 0) {
    return (
      <div style={{
        background: 'linear-gradient(145deg, #EEF3F8, #E0E8F0)',
        borderRadius: 12,
        border: '1px solid var(--df-border)',
        height: 320,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--df-text-muted)',
        fontSize: 14,
      }}>
        <ShopOutlined style={{ fontSize: 42, marginBottom: 8, color: 'var(--df-border)' }} />
        <Text type="secondary">No image available</Text>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        background: '#fff',
        borderRadius: 12,
        border: '1px solid var(--df-border)',
        height: 340,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative',
      }}>
        <img
          src={images[activeIndex].url}
          alt=""
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
        <div style={{
          position: 'absolute',
          bottom: 12,
          right: 12,
          background: 'rgba(11, 58, 92, 0.72)',
          color: '#fff',
          fontSize: 12,
          padding: '4px 10px',
          borderRadius: 8,
        }}>
          {activeIndex + 1} / {images.length}
        </div>
      </div>

      {images.length > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, overflowX: 'auto' }}>
          {images.map((img, i) => (
            <button
              key={img.id || i}
              type="button"
              onClick={() => setActiveIndex(i)}
              aria-label={`View image ${i + 1}`}
              style={{
                width: 72,
                height: 72,
                borderRadius: 8,
                border: i === activeIndex ? '2px solid var(--df-primary)' : '2px solid var(--df-border)',
                overflow: 'hidden',
                cursor: 'pointer',
                flexShrink: 0,
                background: '#F4F7FA',
                padding: 0,
              }}
            >
              <img
                src={`${img.url}?tr=w-72,h-72,fo-auto,f-auto,q-80`}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DealDetailPage() {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  const { data: deal, isLoading, error } = useGetDealQuery(publicId);
  const [vote] = useVoteMutation();
  const [saveDeal] = useSaveDealMutation();
  const [unsaveDeal] = useUnsaveDealMutation();

  if (isLoading) {
    return <div style={{ textAlign: 'center', padding: '120px 0' }}><Spin size="large" tip="Loading deal…" /></div>;
  }

  if (error || !deal) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Title level={4}>Deal not found</Title>
        <Text style={{ display: 'block', marginBottom: 24, color: 'var(--df-text-secondary)' }}>
          This deal may have been removed or expired.
        </Text>
        <Button onClick={() => navigate('/')} size="large" style={{ borderRadius: 8, fontWeight: 600, height: 44 }}>
          Browse Deals
        </Button>
      </div>
    );
  }

  const handleVote = async (id, voteType) => {
    if (!isAuthenticated) return;
    await vote({ dealId: id, vote: voteType });
  };

  const handleSave = async () => {
    if (!isAuthenticated) return;
    if (deal.isSaved) await unsaveDeal(publicId);
    else await saveDeal(publicId);
  };

  const handleVisit = () => {
    window.open(`/api/v1/out/${publicId}`, '_blank', 'noopener,noreferrer');
  };

  const categoryName = entityLabel(deal.category);
  const merchantName = entityLabel(deal.merchant);
  const canEdit = isAuthenticated && user && (
    ['admin', 'moderator'].includes(user.role)
    || (deal.submittedByPublicId && user.id === deal.submittedByPublicId)
  );

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        style={{ marginBottom: 20, color: 'var(--df-text-secondary)', padding: '0 4px', height: 40 }}
      >
        Back to deals
      </Button>

      <div className="df-detail-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(280px, 1fr) 1.25fr',
        gap: 32,
        marginBottom: 32,
      }}>
        <ImageGallery images={deal.images} />

        <div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
            {categoryName && (
              <Tag style={{ borderRadius: 6, border: 'none', background: '#EEF3F8', color: 'var(--df-text-secondary)', padding: '4px 10px' }}>
                {categoryName}
              </Tag>
            )}
            {deal.isFreeShipping && (
              <Tag style={{ borderRadius: 6, border: 'none', background: 'rgba(15,123,90,0.1)', color: 'var(--df-success)', padding: '4px 10px' }}>
                Free Shipping
              </Tag>
            )}
            <Tag style={{ borderRadius: 6, border: 'none', background: '#F3EDE6', color: '#8A5A2B', padding: '4px 10px' }}>
              {CONDITION_LABELS[deal.condition] || deal.condition || 'New'}
            </Tag>
          </div>

          <Title level={3} style={{ margin: '0 0 16px', lineHeight: 1.25, fontFamily: 'var(--df-font-display)' }}>
            {deal.title}
          </Title>

          <PriceTag price={deal.priceMinor} retailPrice={deal.retailPriceMinor} currency={deal.currency} size="large" />

          {deal.description && (
            <Paragraph style={{ marginTop: 18, color: 'var(--df-text-secondary)', fontSize: 15, lineHeight: 1.7 }}>
              {deal.description}
            </Paragraph>
          )}

          <div style={{ display: 'flex', gap: 24, marginTop: 22, flexWrap: 'wrap' }}>
            <div>
              <Text style={{ fontSize: 12, display: 'block', marginBottom: 2, color: 'var(--df-text-muted)' }}>Merchant</Text>
              <Text strong style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <ShopOutlined style={{ color: 'var(--df-text-muted)', fontSize: 14 }} />
                {merchantName || 'N/A'}
              </Text>
            </div>
            {deal.submittedBy && (
              <div>
                <Text style={{ fontSize: 12, display: 'block', marginBottom: 2, color: 'var(--df-text-muted)' }}>Posted by</Text>
                <Text strong>{deal.submittedBy}</Text>
              </div>
            )}
            <div>
              <Text style={{ fontSize: 12, display: 'block', marginBottom: 2, color: 'var(--df-text-muted)' }}>Submitted</Text>
              <Text strong style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <CalendarOutlined style={{ color: 'var(--df-text-muted)', fontSize: 14 }} />
                {new Date(deal.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
              </Text>
            </div>
          </div>

          <div style={{
            marginTop: 28,
            padding: '18px 20px',
            background: 'rgba(255,255,255,0.92)',
            border: '1px solid var(--df-border)',
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
          }}>
            <VoteButtons up={deal.upvotes} down={deal.downvotes} userVote={deal.userVote} dealId={publicId} onVote={handleVote} />

            <Button
              type="text"
              icon={deal.isSaved ? <HeartFilled style={{ color: 'var(--df-error)' }} /> : <HeartOutlined />}
              onClick={handleSave}
              aria-pressed={!!deal.isSaved}
              style={{ color: deal.isSaved ? 'var(--df-error)' : 'var(--df-text-secondary)', height: 44 }}
            >
              {deal.isSaved ? 'Saved' : 'Save'}
            </Button>

            <Button
              type="text"
              icon={<ShareAltOutlined />}
              onClick={() => navigator.clipboard?.writeText(window.location.href)}
              style={{ color: 'var(--df-text-secondary)', height: 44 }}
            >
              Share
            </Button>

            {canEdit && (
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => navigate(`/deals/${publicId}/edit`)}
                style={{ color: 'var(--df-text-secondary)', height: 44 }}
              >
                Edit
              </Button>
            )}

            <Button
              type="primary"
              icon={<ShoppingCartOutlined />}
              size="large"
              onClick={handleVisit}
              style={{
                marginLeft: 'auto',
                borderRadius: 8,
                fontWeight: 600,
                height: 46,
                padding: '0 28px',
                background: 'var(--df-accent)',
                borderColor: 'var(--df-accent)',
              }}
            >
              Get this Deal
            </Button>
          </div>
        </div>
      </div>

      <div style={{
        background: 'var(--df-surface-elevated)',
        borderRadius: 12,
        border: '1px solid var(--df-border)',
        padding: 24,
        marginBottom: 24,
      }}>
        <Title level={5} style={{ margin: '0 0 16px', fontFamily: 'var(--df-font-display)' }}>Price History</Title>
        <PriceHistoryChart data={deal.priceObservations || []} loading={false} />
      </div>

      <div style={{
        background: 'var(--df-surface-elevated)',
        borderRadius: 12,
        border: '1px solid var(--df-border)',
        padding: 24,
        marginBottom: 48,
      }}>
        <CommentSection dealPublicId={publicId} />
      </div>

      <style>{`
        @media (max-width: 768px) {
          .df-detail-grid { grid-template-columns: 1fr !important; gap: 20px !important; }
        }
      `}</style>
    </div>
  );
}
